/**
 * CanvasScreenSearch - Search popover for finding and navigating to screens on canvas
 *
 * Opens via click on SearchIcon button or Cmd/Ctrl+F hotkey.
 * Filters screens by name or entityId, navigates camera on selection.
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  IconButton,
  Popover,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Typography,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import { v2ScreenRegistry } from '@modules/reactFlow/services/onboarding/V2ScreenRegistry';

interface CanvasScreenSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScreenSelected: (screenId: string) => void;
}

const CanvasScreenSearch: React.FC<CanvasScreenSearchProps> = ({
  open,
  onOpenChange,
  onScreenSelected,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  // Bump on each popover open so newly added screens appear
  const [openCount, setOpenCount] = useState(0);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input and refresh screen list when popover opens
  useEffect(() => {
    if (open) {
      setOpenCount((c) => c + 1);
      // Small delay for Popover mount animation
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setSearchQuery('');
    }
  }, [open]);

  // Get all screens and filter
  const screens = useMemo(() => {
    const all = v2ScreenRegistry.getAll();
    if (!searchQuery.trim()) return all;

    const query = searchQuery.toLowerCase();
    return all.filter((screen) => {
      const name = typeof screen.name === 'string' ? screen.name.toLowerCase() : '';
      const id = (screen.entityId || screen.id || '').toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, openCount]);

  const handleSelect = (screenId: string) => {
    onScreenSelected(screenId);
    onOpenChange(false);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={() => onOpenChange(!open)}
        size="small"
        data-testid="canvas-screen-search-btn"
        sx={{
          bgcolor: 'background.paper',
          boxShadow: theme.shadows[2],
          '&:hover': { bgcolor: 'action.hover' },
          width: 36,
          height: 36,
        }}
      >
        <SearchIcon fontSize="small" />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 300,
              maxHeight: 420,
              boxShadow: theme.shadows[4],
            },
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <TextField
            inputRef={inputRef}
            size="small"
            fullWidth
            placeholder="Search screens..."
            data-testid="canvas-screen-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              // Enter selects first result
              if (e.key === 'Enter' && screens.length > 0) {
                handleSelect(screens[0].entityId || screens[0].id || '');
              }
              // Escape closes popover
              if (e.key === 'Escape') {
                handleClose();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <List
          dense
          data-testid="canvas-screen-search-list"
          sx={{
            maxHeight: 340,
            overflow: 'auto',
            pt: 0,
          }}
        >
          {screens.length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No screens found
              </Typography>
            </Box>
          ) : (
            screens.map((screen) => {
              const screenName = typeof screen.name === 'string' ? screen.name : 'Unnamed Screen';
              const screenType = (screen as any).screenType || '';
              return (
                <ListItemButton
                  key={screen.entityId || screen.id}
                  onClick={() => handleSelect(screen.entityId || screen.id || '')}
                  data-testid={`canvas-screen-search-item-${screenType || screenName}`}
                  sx={{ px: 2 }}
                >
                  <ListItemText
                    primary={screenName}
                    secondary={screenType}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                </ListItemButton>
              );
            })
          )}
        </List>
      </Popover>
    </>
  );
};

export default CanvasScreenSearch;
